import { createContext, useContext, useCallback, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "./AuthContext";
import { trackEvent } from "../lib/track";

const Top8Context = createContext(null);
export const TOP8_MAX = 8;

// Top 8 is the curated profile showcase — its OWN collection (user.top8),
// independent of My List and Favorites. Max 8, editable, swap-when-full.
export function Top8Provider({ children }) {
  const { user, patchUser } = useAuth();
  const list = (user && user.top8) || [];
  const [picker, setPicker] = useState(null); // { newItem, slots:[items] }
  const [busy, setBusy] = useState(false);

  const inTop8 = useCallback((id) => list.includes(id), [list]);
  const isFull = list.length >= TOP8_MAX;

  const removeFromTop8 = useCallback(async (item) => {
    const id = typeof item === "string" ? item : item.id;
    if (!list.includes(id)) return;
    const prev = list;
    patchUser({ top8: prev.filter((x) => x !== id) });
    try { await api.delete(`/top8/${id}`); toast("Removed from your Top 8"); }
    catch { patchUser({ top8: prev }); toast.error("Could not update your Top 8"); }
  }, [list, patchUser]);

  const addToTop8 = useCallback(async (item) => {
    const id = typeof item === "string" ? item : item.id;
    if (list.includes(id)) { removeFromTop8(item); return; } // toggle off
    if (list.length < TOP8_MAX) {
      const prev = list;
      patchUser({ top8: [...prev, id] });
      try { await api.post("/top8", { id }); trackEvent("top8_add", { title_id: id }); toast.success("Added to your Top 8"); }
      catch { patchUser({ top8: prev }); toast.error("Could not update your Top 8"); }
      return;
    }
    // Full: open a picker to choose which entry to swap out.
    setBusy(true);
    try {
      const { data } = await api.get("/top8");
      setPicker({ newItem: item, slots: (data.items || []).slice(0, TOP8_MAX) });
    } catch { toast.error("Could not open your Top 8."); }
    finally { setBusy(false); }
  }, [list, patchUser, removeFromTop8]);

  const confirmReplace = useCallback(async (removeId) => {
    const item = picker?.newItem;
    setPicker(null);
    if (!item) return;
    const prev = list;
    const next = prev.filter((x) => x !== removeId).concat(item.id);
    patchUser({ top8: next });
    try {
      await api.put("/top8", { ids: next });
      trackEvent("top8_add", { title_id: item.id });
      toast.success(`Added "${item.title}" to your Top 8`);
    } catch { patchUser({ top8: prev }); toast.error("Could not update your Top 8"); }
  }, [picker, list, patchUser]);

  return (
    <Top8Context.Provider value={{ inTop8, isFull, addToTop8, removeFromTop8, busy }}>
      {children}
      {picker && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4" data-testid="top8-replace-modal" onClick={() => setPicker(null)}>
          <div className="social-panel w-full max-w-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-dotted border-[var(--border-subtle)] px-4 py-3">
              <h2 className="font-head text-lg text-[var(--text-primary)]" data-testid="top8-replace-title">Your Top 8 is full — pick one to replace</h2>
              <button onClick={() => setPicker(null)} className="social-link" data-testid="top8-replace-cancel" aria-label="Cancel"><X size={18} /></button>
            </div>
            <p className="px-4 pt-3 text-sm text-[var(--text-secondary)]">
              Replacing an entry drops in <span className="font-bold text-[var(--accent)]">{picker.newItem.title}</span>. (This only changes your Top 8 — not My List or Favorites.)
            </p>
            <div className="grid grid-cols-4 gap-3 p-4">
              {picker.slots.map((it) => (
                <button key={it.id} onClick={() => confirmReplace(it.id)} className="group text-left" data-testid={`top8-replace-slot-${it.id}`}>
                  <div className="aspect-[2/3] overflow-hidden rounded border border-dotted border-[var(--border-subtle)] transition group-hover:border-[var(--accent)]">
                    <img src={it.poster || it.img} alt={it.title} className="h-full w-full object-cover transition group-hover:opacity-70" />
                  </div>
                  <p className="mt-1 truncate text-[11px] font-bold text-[var(--text-primary)]" title={it.title}>{it.title}</p>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--accent)]">Replace</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </Top8Context.Provider>
  );
}

export const useTop8 = () => useContext(Top8Context);
